import type { FaceBox } from './faceDetector';

/**
 * Blink detection for the "blink 3 times" capture used by BOTH camera steps.
 *
 * SCOPE — read this before relying on it for anything:
 *
 * This is UX, not security. Counting blinks in the applicant's own browser makes the check
 * feel real and deters the laziest attack (holding up a printed photo), but anything computed
 * on the client can be lied to: a replayed video of somebody blinking passes, and a modified
 * client can report three blinks with no camera at all. The actual anti-spoof check stays
 * where it can be trusted — the server-side liveness API that scores the captured frame.
 * Nothing here is allowed to replace it.
 *
 * WHY THIS MODEL AND NOT THE RING'S. BlazeFace (faceDetector.ts) returns only a bounding box;
 * eyelids need landmarks. MediaPipe's FaceLandmarker returns those AND blendshape scores —
 * `eyeBlinkLeft` / `eyeBlinkRight` as 0→1 values — which is a far more robust signal than
 * measuring eyelid geometry by hand. It also yields the face box as a by-product, so a step
 * using this runs ONE model rather than two.
 *
 * BlazeFace is not redundant: it is the fallback for when this model cannot load, which is the
 * difference between a degraded ring and an applicant stuck behind a red one.
 */

/** What one frame's inference tells us. */
export interface BlinkFrame {
  /** Highest of the two eyes' blink scores, 0→1. For the FIRST face only. */
  blinkScore: number;
  /**
   * EVERY face found, in video pixels, so the ring can reuse this one inference — and so
   * "more than one person in frame" is detectable at all. Previously the model was capped at
   * one face, which silently made that check unreachable: a second person simply was not seen.
   */
  faces: FaceBox[];
  /**
   * Whether the face is turned towards the camera.
   *
   * The landmarker happily tracks a profile, and the blink blendshapes keep working on one —
   * so without this a face turned to the side blinked its way through the gate. A side-on
   * selfie is useless for face matching, which is the entire point of the capture.
   */
  facingCamera: boolean;
}

/**
 * Blink thresholds, with a deliberate gap between them.
 *
 * A single threshold would count a dozen blinks from one slow eyelid as the score wobbles
 * across the line. Requiring the score to rise above CLOSED and then fall back under OPEN
 * (hysteresis) makes one blink count exactly once.
 *
 * 0.5 / 0.3 rather than something tighter: a real blink drives the score close to 1.0, while
 * a narrowed or squinting eye sits well under 0.3, so the gap is wide enough to be immune to
 * frame-to-frame noise without missing a fast blink.
 */
const CLOSED_THRESHOLD = 0.5;
const OPEN_THRESHOLD = 0.3;

/** Tracks eyelid state across frames so each blink is counted once. */
export class BlinkCounter {
  private eyesClosed = false;
  private count = 0;

  /** Feeds one frame's score in; returns the running blink count. */
  push(blinkScore: number): number {
    if (!this.eyesClosed && blinkScore >= CLOSED_THRESHOLD) {
      this.eyesClosed = true;
    } else if (this.eyesClosed && blinkScore <= OPEN_THRESHOLD) {
      // Counted on the OPEN, not the close: a blink is only a blink once the eye reopens,
      // and counting on the close would score someone who simply shut their eyes.
      this.eyesClosed = false;
      this.count += 1;
    }

    return this.count;
  }

  reset(): void {
    this.eyesClosed = false;
    this.count = 0;
  }
}

// ── The model ─────────────────────────────────────────────────────────────────────────

interface LandmarkerResult {
  faceLandmarks: { x: number; y: number }[][];
  faceBlendshapes?: { categories: { categoryName: string; score: number }[] }[];
}

interface Landmarker {
  detectForVideo(video: HTMLVideoElement, timestampMs: number): LandmarkerResult;
}

let landmarkerPromise: Promise<Landmarker | null> | null = null;

async function loadLandmarker(): Promise<Landmarker | null> {
  try {
    const { FaceLandmarker, FilesetResolver } = await import('@mediapipe/tasks-vision');
    const fileset = await FilesetResolver.forVisionTasks('/mediapipe/wasm');
    const landmarker = await FaceLandmarker.createFromOptions(fileset, {
      baseOptions: {
        modelAssetPath: '/models/face_landmarker.task',
        delegate: 'GPU',
      },
      runningMode: 'VIDEO',
      // TWO, not one. At one the model returns the strongest face and nothing else, so a
      // second person in frame was invisible and the multiple-faces check could never fire.
      // Two is enough to detect "more than one" without paying for more.
      numFaces: 2,
      // The blink scores live here. Without this the model returns landmarks only and the
      // eyelid opening would have to be measured by hand.
      outputFaceBlendshapes: true,
    });
    return landmarker as unknown as Landmarker;
  } catch {
    // Asset 404, blocked request, unsupported device. The caller keeps manual capture, which
    // is exactly today's behaviour — blink capture is additive and never a prerequisite.
    return null;
  }
}

/**
 * Starts fetching the model, and resolves TRUE once it is usable / FALSE if it cannot load.
 *
 * The download is ~6.5MB (WASM plus the model). It used to begin on the first sampled frame,
 * i.e. after the camera was already on screen, so the applicant watched an unresponsive ring
 * for several seconds. Calling this the moment the step mounts moves that wait to a point the
 * applicant is already spending — reading the guidance screen, answering the permission
 * prompt — rather than adding to it.
 *
 * It resolves rather than returning void because the caller does not merely want the download
 * started, it wants to know when the gate is live: the camera is held closed until then, so
 * there is never a live preview that nothing is checking.
 *
 * Safe to call repeatedly — the load is memoised, so later calls join the same promise and a
 * second camera step in the same run resolves instantly.
 */
export function warmUpBlinkModel(): Promise<boolean> {
  landmarkerPromise ??= loadLandmarker();
  return landmarkerPromise.then((landmarker) => landmarker !== null);
}

/**
 * One frame's blink score and face box.
 *
 *   `undefined` — the landmarker is UNAVAILABLE (failed to load, unsupported device). The
 *                 caller must fall back to its normal detector; treating this as "no face"
 *                 would pin the ring red with no way out, because the applicant cannot fix
 *                 a model that did not download.
 *   `null`      — the landmarker ran and saw no face. That IS a red ring.
 *
 * The distinction is the whole reason this returns two kinds of nothing.
 *
 * `timestampMs` must strictly increase — MediaPipe's VIDEO mode rejects a timestamp that
 * does not advance.
 */
export async function readBlinkFrame(
  video: HTMLVideoElement,
  timestampMs: number,
): Promise<BlinkFrame | null | undefined> {
  landmarkerPromise ??= loadLandmarker();
  const landmarker = await landmarkerPromise;
  if (!landmarker) return undefined;

  try {
    const result = landmarker.detectForVideo(video, timestampMs);
    const allLandmarks = result.faceLandmarks ?? [];
    const landmarks = allLandmarks[0];
    if (!landmarks || landmarks.length === 0) return null;

    const categories = result.faceBlendshapes?.[0]?.categories ?? [];
    let blinkScore = 0;
    for (const category of categories) {
      if (category.categoryName === 'eyeBlinkLeft' || category.categoryName === 'eyeBlinkRight') {
        // The MAXIMUM of the two eyes, not the average: a wink would average to ~0.5 and sit
        // permanently between the thresholds, and one eye closing is enough to be a blink
        // worth counting for our purposes.
        blinkScore = Math.max(blinkScore, category.score);
      }
    }

    const faces: FaceBox[] = [];
    for (const set of allLandmarks) {
      const box = boundingBoxOf(set, video);
      if (box) faces.push(box);
    }

    return { blinkScore, faces, facingCamera: isFacingCamera(landmarks) };
  } catch {
    // A frame can fail for ordinary reasons (mid-resize, not yet painted). That is "nothing
    // to report", not "no face" — same reasoning as above.
    return undefined;
  }
}

/**
 * Canonical FaceMesh landmark indices used for the head-turn test.
 *
 * The mesh is a fixed topology, so these indices always mean the same points: the nose tip
 * and the two outer edges of the face at cheek height.
 */
const NOSE_TIP = 1;
const FACE_EDGE_A = 234;
const FACE_EDGE_B = 454;

/**
 * How lopsided the face may be before it counts as turned away.
 *
 * Measured as the ratio between the nose's horizontal distance to each side of the face.
 * Dead-on gives ~1.0; turning the head shrinks the near side and grows the far one. 0.55
 * allows a natural, relaxed head position — nobody holds a phone perfectly square — while
 * rejecting the three-quarter and profile views that a face match cannot use.
 */
const MIN_SYMMETRY_RATIO = 0.55;

/**
 * Whether the head is turned towards the camera, from landmark symmetry.
 *
 * Symmetry rather than the model's transformation matrix: it needs no extra model output, it
 * is trivially inspectable, and it degrades sensibly — a face at a slight angle scores near
 * 1.0 and passes, which is what a real applicant holding a phone looks like.
 */
function isFacingCamera(landmarks: { x: number; y: number }[]): boolean {
  const nose = landmarks[NOSE_TIP];
  const edgeA = landmarks[FACE_EDGE_A];
  const edgeB = landmarks[FACE_EDGE_B];
  if (!nose || !edgeA || !edgeB) {
    // Landmarks missing — say nothing rather than blocking on an absent measurement.
    return true;
  }

  const toA = Math.abs(nose.x - edgeA.x);
  const toB = Math.abs(edgeB.x - nose.x);
  const wider = Math.max(toA, toB);
  if (wider === 0) return true;

  return Math.min(toA, toB) / wider >= MIN_SYMMETRY_RATIO;
}

/**
 * The face's pixel bounding box from its landmarks, so the ring's framing check can reuse
 * this inference instead of running a second model alongside it.
 *
 * Landmarks are normalised 0→1 against the frame, so they scale to pixels directly.
 */
function boundingBoxOf(
  landmarks: { x: number; y: number }[],
  video: HTMLVideoElement,
): FaceBox | null {
  let minX = 1;
  let minY = 1;
  let maxX = 0;
  let maxY = 0;

  for (const point of landmarks) {
    if (point.x < minX) minX = point.x;
    if (point.y < minY) minY = point.y;
    if (point.x > maxX) maxX = point.x;
    if (point.y > maxY) maxY = point.y;
  }

  const { videoWidth: w, videoHeight: h } = video;
  if (w === 0 || h === 0 || maxX <= minX || maxY <= minY) return null;

  return {
    x: minX * w,
    y: minY * h,
    width: (maxX - minX) * w,
    height: (maxY - minY) * h,
  };
}
